import { useNavigate } from '@tanstack/react-router'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useFolder } from '@/modules/folders'
import { type Project, useProjectsMutations } from '@/modules/projects'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'
import { getFolderPath } from '@/utils'

const dialogId = 'delete-projects'

export const DeleteProjectsDialog = () => {
  const { successToast, errorToast } = useToast()
  const navigate = useNavigate()
  const open = useDialogIsOpen(dialogId)
  const projectsToDelete = (useDialogData() as Project[] | undefined) || []
  const { closeDialog } = useDialogActions()
  const total = projectsToDelete?.length || 0
  const project = total === 1 ? projectsToDelete[0] : null
  const label = project ? 'Project' : 'Projects'

  const folder = useFolder()

  const { deleteProjects } = useProjectsMutations()
  const handleSubmit = () => {
    deleteProjects(projectsToDelete)
      .then(() => successToast(`${label} deleted successfully`))
      .catch((error) => errorToast(`Failed to delete projects`, error))
    closeDialog(true)

    if (projectsToDelete[0].archivedAt) {
      navigate({ to: '/projects', search: { view: 'archived' } })
      return
    }

    const folderToRedirect = folder || projectsToDelete[0].folder
    navigate({
      to: folderToRedirect ? getFolderPath(folderToRedirect) : '/projects',
    })
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {label}?</DialogTitle>
          <DialogDescription>
            This will permanently delete{' '}
            {project ? `"${project?.name}"` : `${total} projects`} and all
            associated statements and processed data. This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-4">
          <Button theme="outline" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} theme="danger">
            Delete {label}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

DeleteProjectsDialog.id = dialogId
